import {
  DocumentSnapshot,
  QueryDocumentSnapshot,
  Timestamp,
  collection,
  doc,
  onSnapshot,
} from 'firebase/firestore';
import {
  createContext,
  Dispatch,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { usePersistedJsonState } from '@superfunnel/react-hooks/usePersistedState';
import { AccountAiSettings } from '@superfunnel/types/account-ai-settings';
import { AccountBrand } from '@superfunnel/types/account-brand';
import { AccountConfig } from '@superfunnel/types/account-config';
import { AdsAccount } from '@superfunnel/types/ads-account';
import {
  AnalyticsFilters,
  EmptyAnalyticsFilters,
} from '@superfunnel/types/analytics-filter';
import { Domain } from '@superfunnel/types/domain';
import { DynamicPage } from '@superfunnel/types/dynamic-page';
import { Form } from '@superfunnel/types/form';
import { GlobalSection } from '@superfunnel/types/global-section';
import { Quiz } from '@superfunnel/types/quiz';
import { SectionType } from '@superfunnel/types/section';
import {
  SectionTypeConfig,
  SectionTypeStructureFieldList,
} from '@superfunnel/types/section-type-config';
import { AccountSubscription } from '@superfunnel/types/subscription';
import { TeamMember } from '@superfunnel/types/team-member';

import { db } from '../Firebase';
import { getAccountAiSettingsDocRef } from '../services/AccountAiSettingsService';
import { getAccountBrandDocRef } from '../services/AccountBrandService';
import { getAccountConfigDocRef } from '../services/AccountConfigService';
import {
  updateAccountAiSettings as updateAccountAiSettingsApi,
  updateAccountBrand as updateAccountBrandApi,
  updateAccountConfig as updateAccountConfigApi,
} from '../services/AccountsService';
import { activeAdsAccountsQuery } from '../services/AdsAccountsService';
import { getAnalyticsByDates } from '../services/AnalyticsService';
import { getDomainsColRef } from '../services/DomainsService';
import { dynamicPagesQuery } from '../services/DynamicPagesService';
import { formsQuery } from '../services/FormsService';
import { globalSectionsQuery } from '../services/GlobalSectionsService';
import { quizzesQuery } from '../services/QuizService';
import { getAccountSectionTypes } from '../services/SectionTypesService';
import { approvedTeamMembersQuery } from '../services/TeamMembersService';
import { analyticsRangeToDates, getPrimaryDomain } from '../utils';
import { safeLocalStorage } from '../utils/local-storage';
import { useAnalytics } from './AnalyticsContext';
import { useLazySubscriptions } from './useLazySubscriptions';
import { UserContext } from './UserContext';

import { AnalyticsTypeOption } from '@/components/analytics/StatCellValues';

export type AnalyticsCustomRange = {
  startDate: string;
  endDate: string;
};

export type AnalyticsRange =
  'today' | 'last7Days' | 'last30Days' | AnalyticsCustomRange;

interface DashboardContextProps {
  dynamicPages?: QueryDocumentSnapshot<DynamicPage, DynamicPage>[];
  isNewAccount: boolean;
  // selectedDynamicPage?: QueryDocumentSnapshot<DynamicPage, DynamicPage>;
  // setSelectedDynamicPage: (
  //   page: QueryDocumentSnapshot<DynamicPage, DynamicPage> | undefined,
  // ) => void;
  teamMembers?: QueryDocumentSnapshot<TeamMember, TeamMember>[];
  forms?: QueryDocumentSnapshot<Form, Form>[];
  quizzes?: QueryDocumentSnapshot<Quiz, Quiz>[];
  globalSections?: QueryDocumentSnapshot<GlobalSection, GlobalSection>[];

  allDomains?: QueryDocumentSnapshot<Domain, Domain>[];
  domains?: QueryDocumentSnapshot<Domain, Domain>[];
  primaryDomain?: QueryDocumentSnapshot<Domain, Domain>;
  adsAccounts?: QueryDocumentSnapshot<AdsAccount, AdsAccount>[];
  adsAccountsAccountId?: string;

  sectionTypeConfigs?: QueryDocumentSnapshot<SectionTypeConfig>[];
  getSectionTypeConfig: (
    type: SectionType | string,
  ) => SectionTypeConfig | undefined;
  getSectionListItemFieldConfig: (
    sectionType: SectionType | string,
    field: string,
  ) => SectionTypeStructureFieldList | undefined;

  analyticsType: AnalyticsTypeOption | null;

  accountBrand?: AccountBrand;
  updateAccountBrand: (config: Partial<AccountBrand>) => void;

  accountAiSettings?: AccountAiSettings;
  updateAccountAiSettings: (config: Partial<AccountAiSettings>) => void;

  accountConfig?: AccountConfig;
  isAccountConfigLoaded: boolean;
  updateAccountConfig: (config: Partial<AccountConfig>) => void;

  subscription?: DocumentSnapshot<AccountSubscription>;
  isSubscriptionLoaded: boolean;
  hasActiveSubscription: boolean;

  analyticsRange: AnalyticsRange;
  setAnalyticsRange: (range: AnalyticsRange) => void;

  analyticsFilters: AnalyticsFilters;
  setAnalyticsFilters: Dispatch<SetStateAction<AnalyticsFilters>>;
  resetAnalyticsFilters: () => void;

  /** Account-level analytics for the selected date range and filters */
  customRangeAnalytics?: any;

  // dynamicPageWebsitePreviewToken?: string;
  // fetchDynamicPageWebsitePreviewToken: () => Promise<any>;
}

export const DashboardContext = createContext<DashboardContextProps>({
  isNewAccount: true,
  getSectionTypeConfig: () => undefined,
  getSectionListItemFieldConfig: () => undefined,

  // setSelectedDynamicPage: () => {},
  updateAccountBrand: () => {},
  updateAccountAiSettings: () => {},
  updateAccountConfig: () => {},
  analyticsType: null,
  isSubscriptionLoaded: false,
  isAccountConfigLoaded: false,
  hasActiveSubscription: false,

  analyticsRange: 'last30Days',
  setAnalyticsRange: () => {},

  analyticsFilters: EmptyAnalyticsFilters,
  setAnalyticsFilters: () => {},
  resetAnalyticsFilters: () => {},

  customRangeAnalytics: undefined,

  // fetchDynamicPageWebsitePreviewToken: async () => '',
});

export function DashboardContextProvider(props: any) {
  const { selectedAccount } = useContext(UserContext);
  const { requested, track } = useLazySubscriptions<DashboardContextProps>();
  const { setSessionTag } = useAnalytics();

  const [dynamicPages, setDynamicPages] =
    useState<QueryDocumentSnapshot<DynamicPage, DynamicPage>[]>();
  const [forms, setForms] = useState<QueryDocumentSnapshot<Form, Form>[]>();
  const [quizzes, setQuizzes] = useState<QueryDocumentSnapshot<Quiz, Quiz>[]>();
  const [globalSections, setGlobalSections] =
    useState<QueryDocumentSnapshot<GlobalSection, GlobalSection>[]>();
  const [allDomains, setAllDomains] =
    useState<QueryDocumentSnapshot<Domain, Domain>[]>();
  const [accountBrand, setAccountBrand] = useState<AccountBrand>();
  const [accountBrandToSave, setAccountBrandToSave] =
    useState<Partial<AccountBrand>>();
  const [accountAiSettings, setAccountAiSettings] =
    useState<AccountAiSettings>();
  const [accountAiSettingsToSave, setAccountAiSettingsToSave] =
    useState<Partial<AccountAiSettings>>();
  const [accountConfig, setAccountConfig] = useState<AccountConfig>();
  const [accountConfigToSave, setAccountConfigToSave] = useState<{
    accountId: string;
    config: Partial<AccountConfig>;
  }>();
  const [subscription, setSubscription] =
    useState<DocumentSnapshot<AccountSubscription>>();
  const [subscriptionAccountId, setSubscriptionAccountId] = useState<string>();
  const isSubscriptionLoaded =
    !!selectedAccount && subscriptionAccountId === selectedAccount.id;
  const [accountConfigAccountId, setAccountConfigAccountId] =
    useState<string>();
  const isAccountConfigLoaded =
    !!selectedAccount && accountConfigAccountId === selectedAccount.id;
  const [sectionTypeConfigs, setSectionTypeConfigs] =
    useState<QueryDocumentSnapshot<SectionTypeConfig>[]>();
  const [analyticsRange, setAnalyticsRange] =
    usePersistedJsonState<AnalyticsRange>(
      'analyticsRange',
      'last30Days',
      safeLocalStorage,
    );
  const [analyticsFilters, setAnalyticsFilters] =
    usePersistedJsonState<AnalyticsFilters>(
      'analyticsFilters:v2',
      EmptyAnalyticsFilters,
      safeLocalStorage,
    );
  const analyticsType: AnalyticsTypeOption | null = null;
  const [teamMembers, setTeamMembers] =
    useState<QueryDocumentSnapshot<TeamMember, TeamMember>[]>();
  const [adsAccountsSnapshot, setAdsAccountsSnapshot] = useState<{
    accountId: string;
    adsAccounts: QueryDocumentSnapshot<AdsAccount, AdsAccount>[];
  }>();
  const adsAccounts = adsAccountsSnapshot?.adsAccounts;
  const adsAccountsAccountId = adsAccountsSnapshot?.accountId;

  const domains = useMemo(() => {
    return allDomains?.filter((d) => d.data().status === 'approved');
  }, [allDomains]);

  const primaryDomain = useMemo(() => {
    return getPrimaryDomain(domains);
  }, [domains]);

  const hasActiveSubscription = useMemo(() => {
    const subscriptionData = subscription?.data();
    return (
      subscriptionData?.status === 'active' ||
      subscriptionData?.status === 'on_trial' ||
      subscriptionData?.status === 'past_due'
    );
  }, [subscription]);

  useEffect(() => {
    const subscriptionData = subscription?.data();
    if (subscriptionData) {
      setSessionTag(
        'plan',
        subscriptionData.planName || subscriptionData.planType,
      );
      setSessionTag('plan_status', subscriptionData.status);
    }
  }, [subscription, setSessionTag]);

  const resetAnalyticsFilters = useCallback(() => {
    setAnalyticsFilters(EmptyAnalyticsFilters);
  }, [setAnalyticsFilters]);

  // Account-level analytics data (used by DynamicPagesTable, QuizFunnelsTable, etc.)
  const [customRangeAnalytics, setCustomRangeAnalytics] = useState<any>();

  useEffect(() => {
    setCustomRangeAnalytics(undefined);

    if (!selectedAccount) {
      return;
    }

    const { startDate, endDate } = analyticsRangeToDates(analyticsRange);

    let relevant = true;
    getAnalyticsByDates(
      selectedAccount.id,
      'account',
      undefined,
      startDate,
      endDate,
    )
      .then((res) => {
        if (!relevant) return;

        setCustomRangeAnalytics(res);
      })
      .catch((e) => {
        console.error('Failed to fetch analytics for custom range', e);
      });

    return () => {
      relevant = false;
    };
  }, [selectedAccount, analyticsRange]);

  const isNewAccount = useMemo(() => {
    const createdAt: Timestamp = selectedAccount?.data().createdAt;
    if (!createdAt) return true;

    const createdDate = createdAt.toDate();
    const isNewThreshold = new Date('2025-11-24T00:00:00Z');

    return createdDate >= isNewThreshold;
  }, [selectedAccount]);

  useEffect(() => {
    if (!selectedAccount || !requested.dynamicPages) {
      setDynamicPages(undefined);
      return;
    }

    const accountId = selectedAccount.id;
    const unsubscribe = onSnapshot(
      dynamicPagesQuery(accountId),
      (dynamicPages) => {
        setDynamicPages(dynamicPages.docs);
      },
    );

    return () => {
      unsubscribe();
    };
  }, [selectedAccount, requested.dynamicPages]);

  useEffect(() => {
    if (!selectedAccount || !requested.domains) {
      setAllDomains(undefined);
      return;
    }

    const accountId = selectedAccount.id;
    const domainsColRef = getDomainsColRef(accountId);

    const unsubscribe = onSnapshot(domainsColRef, (domains) => {
      setAllDomains(domains.docs);
    });

    return () => {
      unsubscribe();
    };
  }, [selectedAccount, requested.domains]);

  useEffect(() => {
    if (!selectedAccount || !requested.sectionTypeConfigs) {
      setSectionTypeConfigs(undefined);
      return;
    }

    const accountId = selectedAccount.id;

    let relevant = true;
    getAccountSectionTypes(accountId).then((sectionTypes) => {
      if (!relevant) return;
      setSectionTypeConfigs(sectionTypes.docs);
    });

    return () => {
      relevant = false;
    };
  }, [selectedAccount, requested.sectionTypeConfigs]);

  useEffect(() => {
    if (!selectedAccount || !requested.forms) {
      setForms(undefined);
      return;
    }

    const accountId = selectedAccount.id;
    const unsubscribe = onSnapshot(formsQuery(accountId), (forms) => {
      setForms(forms.docs);
    });

    return () => {
      unsubscribe();
    };
  }, [selectedAccount, requested.forms]);

  useEffect(() => {
    if (!selectedAccount || !requested.quizzes) {
      setQuizzes(undefined);
      return;
    }

    const accountId = selectedAccount.id;
    const unsubscribe = onSnapshot(quizzesQuery(accountId), (quizzes) => {
      setQuizzes(quizzes.docs);
    });

    return () => {
      unsubscribe();
    };
  }, [selectedAccount, requested.quizzes]);

  useEffect(() => {
    if (!selectedAccount || !requested.globalSections) {
      setGlobalSections(undefined);
      return;
    }

    const accountId = selectedAccount.id;
    const unsubscribe = onSnapshot(
      globalSectionsQuery(accountId),
      (globalSections) => {
        setGlobalSections(globalSections.docs);
      },
    );

    return () => {
      unsubscribe();
    };
  }, [selectedAccount, requested.globalSections]);

  useEffect(() => {
    if (!selectedAccount || !requested.accountBrand) {
      setAccountBrand(undefined);
      return;
    }

    const accountId = selectedAccount.id;
    const unsubscribe = onSnapshot(
      getAccountBrandDocRef(accountId),
      (accountBrand) => {
        setAccountBrand(
          accountBrand.exists() ? accountBrand.data() : undefined,
        );
      },
    );

    return () => {
      unsubscribe();
    };
  }, [selectedAccount, requested.accountBrand]);

  useEffect(() => {
    if (!selectedAccount || !requested.accountAiSettings) {
      setAccountAiSettings(undefined);
      return;
    }

    const accountId = selectedAccount.id;
    const unsubscribe = onSnapshot(
      getAccountAiSettingsDocRef(accountId),
      (accountAiSettings) => {
        setAccountAiSettings(
          accountAiSettings.exists() ? accountAiSettings.data() : undefined,
        );
      },
    );

    return () => {
      unsubscribe();
    };
  }, [selectedAccount, requested.accountAiSettings]);

  useEffect(() => {
    setAdsAccountsSnapshot(undefined);

    if (!selectedAccount || !requested.adsAccounts) {
      return;
    }

    const accountId = selectedAccount.id;
    const unsubscribe = onSnapshot(
      activeAdsAccountsQuery(accountId),
      (snapshot) => {
        setAdsAccountsSnapshot({
          accountId,
          adsAccounts: snapshot.docs,
        });
      },
    );

    return () => {
      unsubscribe();
    };
  }, [selectedAccount, requested.adsAccounts]);

  const updateAccountBrand = useCallback((data: Partial<AccountBrand>) => {
    setAccountBrandToSave((d) => ({ ...(d || {}), ...data }));
    setAccountBrand((d) => ({ ...(d || {}), ...data }));
  }, []);

  const updateAccountAiSettings = useCallback(
    (data: Partial<AccountAiSettings>) => {
      setAccountAiSettingsToSave((d) => ({ ...(d || {}), ...data }));
      setAccountAiSettings((d) => ({ ...(d || {}), ...data }));
    },
    [],
  );

  useEffect(() => {
    if (!selectedAccount || !accountBrandToSave) return;

    const debounce = setTimeout(() => {
      updateAccountBrandApi(selectedAccount.id, accountBrandToSave);
      setAccountBrandToSave(undefined);
    }, 1500);

    return () => {
      clearTimeout(debounce);
    };
  }, [accountBrandToSave, selectedAccount]);

  useEffect(() => {
    if (!selectedAccount || !accountAiSettingsToSave) return;

    const debounce = setTimeout(() => {
      updateAccountAiSettingsApi(selectedAccount.id, accountAiSettingsToSave);
      setAccountAiSettingsToSave(undefined);
    }, 1500);

    return () => {
      clearTimeout(debounce);
    };
  }, [accountAiSettingsToSave, selectedAccount]);

  useEffect(() => {
    if (!selectedAccount || !requested.accountConfig) {
      setAccountConfig(undefined);
      setAccountConfigAccountId(undefined);
      return;
    }

    const accountId = selectedAccount.id;
    const unsubscribe = onSnapshot(
      getAccountConfigDocRef(accountId),
      (accountConfig) => {
        setAccountConfig(
          accountConfig.exists() ? accountConfig.data() : undefined,
        );
        setAccountConfigAccountId(accountId);
      },
      (error) => {
        console.error('Account config listener failed', error);
        setAccountConfig(undefined);
        setAccountConfigAccountId(accountId);
      },
    );

    return () => {
      unsubscribe();
    };
  }, [selectedAccount, requested.accountConfig]);

  useEffect(() => {
    if (!selectedAccount || !requested.subscription) {
      setSubscription(undefined);
      setSubscriptionAccountId(undefined);
      return;
    }

    const accountId = selectedAccount.id;
    const subscriptionDocRef = doc(
      collection(db, 'accounts', accountId, 'subscriptions'),
      'current',
    ).withConverter<AccountSubscription>({
      toFirestore: (data: AccountSubscription) => data,
      fromFirestore: (snapshot) => snapshot.data() as AccountSubscription,
    });

    const unsubscribe = onSnapshot(subscriptionDocRef, (subscriptionDoc) => {
      setSubscription(subscriptionDoc.exists() ? subscriptionDoc : undefined);
      setSubscriptionAccountId(accountId);
    });

    return () => {
      unsubscribe();
    };
  }, [selectedAccount, requested.subscription]);

  useEffect(() => {
    if (!selectedAccount || !requested.teamMembers) {
      setTeamMembers(undefined);
      return;
    }

    const unsubscribe = onSnapshot(
      approvedTeamMembersQuery(selectedAccount.id),
      (teamMembers) => {
        setTeamMembers(teamMembers.docs);
      },
    );

    return () => {
      unsubscribe();
    };
  }, [selectedAccount, requested.teamMembers]);

  const updateAccountConfig = useCallback(
    (data: Partial<AccountConfig>) => {
      if (!selectedAccount) return;

      const accountId = selectedAccount.id;

      setAccountConfigToSave((pending) => {
        const config =
          pending?.accountId === accountId ? pending.config : undefined;

        return {
          accountId,
          config: {
            ...(config || {}),
            ...data,
            seo: { ...(config?.seo || {}), ...data.seo },
          },
        };
      });
      setAccountConfig((d) => ({
        ...(d || {}),
        ...data,
        seo: { ...(d?.seo || {}), ...data.seo },
      }));
    },
    [selectedAccount],
  );

  useEffect(() => {
    if (!accountConfigToSave) return;

    const { accountId, config } = accountConfigToSave;

    const debounce = setTimeout(() => {
      updateAccountConfigApi(accountId, config);
      setAccountConfigToSave(undefined);
    }, 1500);

    return () => {
      clearTimeout(debounce);
    };
  }, [accountConfigToSave]);

  const getSectionTypeConfig = useCallback(
    (type: SectionType | string) => {
      const onlyMatchingTypes = sectionTypeConfigs?.filter(
        (s) => s.data().type === type,
      );
      return (
        onlyMatchingTypes?.find((s) => s.data().accountId) ||
        onlyMatchingTypes?.[0]
      )?.data();
    },
    [sectionTypeConfigs],
  );

  const getSectionListItemFieldConfig = useCallback(
    (sectionType: SectionType | string, field: string) => {
      const sectionTypeConfig = getSectionTypeConfig(sectionType);
      return sectionTypeConfig?.structure.fields.find(
        (f) => f.type === 'list' && f.name === field,
      ) as SectionTypeStructureFieldList | undefined;
    },
    [getSectionTypeConfig],
  );

  // const fetchDynamicPageWebsitePreviewToken = useCallback(async () => {
  //   if (!selectedAccount || !selectedDynamicPage) {
  //     return;
  //   }
  //   const key = `customPreviewToken-${selectedAccount.id}-${selectedDynamicPage.id}`;
  //   const currentToken = localStorage.getItem(key);

  //   if (currentToken && !isJwtExpired(currentToken)) {
  //     setDynamicPageWebsitePreviewToken(currentToken);
  //     return currentToken;
  //   }

  //   if (tokenFetchingPromises[key]) {
  //     return tokenFetchingPromises[key];
  //   }

  //   const fn = httpsCallable(
  //     functions,
  //     'accounts/campaigns/createPreviewToken',
  //   );

  //   return (tokenFetchingPromises[key] = fn({
  //     accountId: selectedAccount.id,
  //     campaignId: selectedDynamicPage.id,
  //   }).then((res) => {
  //     localStorage.setItem(key, res.data as string);
  //     setDynamicPageWebsitePreviewToken(res.data as string);

  //     return res.data as string;
  //   })).finally(() => {
  //     tokenFetchingPromises[key] = undefined;
  //   });
  // }, [selectedAccount, selectedDynamicPage]);

  return (
    <DashboardContext.Provider
      value={track({
        dynamicPages,
        isNewAccount,
        // selectedDynamicPage,
        // setSelectedDynamicPage,
        forms,
        quizzes,
        globalSections,
        allDomains,
        domains,
        primaryDomain,
        adsAccounts,
        adsAccountsAccountId,
        sectionTypeConfigs,
        getSectionTypeConfig,
        getSectionListItemFieldConfig,
        accountBrand,
        updateAccountBrand,
        accountAiSettings,
        updateAccountAiSettings,
        accountConfig,
        isAccountConfigLoaded,
        updateAccountConfig,
        subscription,
        isSubscriptionLoaded,
        hasActiveSubscription,
        analyticsRange,
        setAnalyticsRange,
        analyticsFilters,
        setAnalyticsFilters,
        resetAnalyticsFilters,
        customRangeAnalytics,
        analyticsType,
        teamMembers,
        // dynamicPageWebsitePreviewToken,
        // fetchDynamicPageWebsitePreviewToken,
      })}
    >
      {props.children}
    </DashboardContext.Provider>
  );
}

export function useDashboardContext() {
  return useContext(DashboardContext);
}
